// packages/frontend/src/shared/api/client.ts
const API_ENDPOINT = process.env.REACT_APP_API_INVOKE_URL;

// API呼び出し関数の型定義
interface CallProtectedApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; // HTTPメソッド
  body?: Record<string, unknown> | null; // リクエストボディ (JSON)
  token?: string | null; // 認証トークン
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  // 他のエラーレスポンスの形式に合わせて追加
}

export async function callProtectedApi<T = unknown>( // レスポンスの型をジェネリックに
  endpoint: string,
  options: CallProtectedApiOptions = {}
): Promise<T | null> {
  // 返り値の型を定義
  const { method = 'POST', body = null, token = null } = options;

  try {
    if (!token) {
      throw new Error('Authentication token is missing');
    }
    if (!API_ENDPOINT) {
      throw new Error(
        'API endpoint URL is not configured (REACT_APP_API_INVOKE_URL)'
      );
    }

    const headers: HeadersInit = {
      // HeadersInit 型を使用
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    const response = await fetch(`${API_ENDPOINT}/${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    });

    if (!response.ok) {
      let errorBody: ApiErrorBody | null = null; // エラーボディの型定義
      try {
        // レスポンスボディがJSONでない場合もあるためtry-catch
        errorBody = await response.json();
      } catch (e) {
        console.debug('Failed to parse error response body as JSON:', e);
      }
      const errorMessage =
        errorBody?.message || errorBody?.error || response.statusText;
      console.error(
        'API request failed:',
        response.status,
        response.statusText,
        errorBody || '(No JSON error body)'
      );
      // エラーオブジェクトにステータスコードを含めるなど、より詳細な情報を提供可能
      const error = new Error(
        `API request failed: ${response.status} ${errorMessage}`
      );
      // (error as any).status = response.status; // 必要であればステータスを追加
      // (error as any).errorBody = errorBody; // 必要であればボディを追加
      throw error;
    }

    // ステータスコード 204 No Content の場合は null を返す
    if (response.status === 204) {
      return null;
    }

    // レスポンスボディを JSON として解析して返す (型 T でキャスト)
    return (await response.json()) as T;
  } catch (error) {
    // エラーの型を適切に処理
    if (error instanceof Error) {
      console.error('API call failed:', error.message);
      throw error; // そのまま再スロー
    } else {
      // 予期しないエラータイプの場合
      console.error('An unexpected error occurred during API call:', error);
      throw new Error('An unexpected error occurred during API call');
    }
  }
}
